<template>
  <div
    :class="
      isFeatureAvailable
        ? 'tw-mb-6 tw-grid tw-grid-cols-12 tw-gap-3 max-xl:tw-grid-cols-10'
        : 'tw-mb-6 tw-grid tw-grid-cols-9 tw-gap-6 max-xl:tw-grid-cols-10'
    "
  >
    <div
      v-if="isSolutionActive(PHISH_SERVICE)"
      class="tw-col-span-3 max-xl:tw-col-span-5 max-md:tw-col-span-10"
      :data-test-id="`${tier}-overview-card-incidents-overview-route`"
    >
      <router-link v-slot="{ href, navigate }" :to="incidentsHref" custom>
        <cl-data-card
          class="tw-h-full"
          data-test-id="dashboard-email-security-card"
          :link="{
            href,
            navigate,
            label: $t('manage-incidents'),
          }"
          :icon-tooltip="useTooltip(ToolTipContent.tooltipEmailSecurity)"
          :card-tooltip="useTooltip(ToolTipContent.tooltipEmailSecurityInfo)"
        >
          <template #icon>
            <cb-icon-email :width="16" />
          </template>
          <template #footer>
            <cl-data-card-footer-loading v-if="loading" />
            <cl-data-card-footer-big-number v-else>
              <span :data-test-id="`${tier}-email-security-incidents-total`">
                {{ formatNumberCompat(totalIncidents) }}
              </span>
            </cl-data-card-footer-big-number>
          </template>
        </cl-data-card>
      </router-link>
    </div>
    <div
      v-else
      class="tw-col-span-3 max-xl:tw-col-span-5 max-md:tw-col-span-10"
    >
      <DashboardEmptyCard v-if="isFeatureAvailable" type="EMAIL_SECURITY" />
      <DashboardEmptyCardLegacy v-else type="EMAIL_SECURITY" />
    </div>
    <div
      v-if="isSolutionActive(PHISHING_SIMULATION)"
      class="tw-col-span-3 max-xl:tw-col-span-5 max-md:tw-col-span-10"
      :data-test-id="`${tier}-phishing-simulation-overview-route`"
    >
      <router-link
        v-slot="{ href, navigate }"
        :to="{ name: `${tier}-phishing_simulation-overview` }"
        custom
      >
        <cl-data-card
          class="tw-h-full"
          data-test-id="dashboard-phishing-simulation-card"
          :link="{
            href,
            navigate,
            label: $t('at-risk-protected'),
          }"
          :icon-tooltip="useTooltip(ToolTipContent.tooltipPhishingSimulation)"
          :card-tooltip="
            useTooltip(ToolTipContent.tooltipPhishingSimulationInfo)
          "
        >
          <template #icon>
            <cb-icon-phishing :width="16" />
          </template>
          <template #footer>
            <cl-data-card-footer-loading v-if="loading" />
            <cl-data-card-footer-big-number v-else>
              <span
                :class="
                  psAtRisk
                    ? 'tw-text-system-error-500'
                    : 'tw-text-system-success-500'
                "
                :data-test-id="
                  psAtRisk
                    ? `${tier}-phishing-simulation-info-number-negative`
                    : `${tier}-phishing-simulation-info-number-positive`
                "
              >
                {{ psAtRisk }}
              </span>
              <cl-data-card-footer-small-number>
                <span :data-test-id="`${tier}-phishing-simulation-total`">
                  / {{ psProtected }}</span
                >
              </cl-data-card-footer-small-number>
            </cl-data-card-footer-big-number>
          </template>
        </cl-data-card>
      </router-link>
    </div>
    <div
      v-else
      class="tw-col-span-3 max-xl:tw-col-span-5 max-md:tw-col-span-10"
    >
      <DashboardEmptyCard
        v-if="isFeatureAvailable"
        type="PHISHING_SIMULATION"
      />
      <DashboardEmptyCardLegacy v-else type="PHISHING_SIMULATION" />
    </div>
    <div
      v-if="isSolutionActive(SAT_SERVICE)"
      class="tw-col-span-3 max-xl:tw-col-span-5 max-md:tw-col-span-10"
      :data-test-id="`${tier}-sat-overview-route`"
    >
      <router-link
        v-slot="{ href, navigate }"
        :to="{ name: `${tier}-security_awareness_training-overview` }"
        custom
      >
        <cl-data-card
          class="tw-h-full"
          data-test-id="dashboard-sat-card"
          :link="{
            href,
            navigate,
            label: $t('sat-at-risk-protected'),
          }"
          :icon-tooltip="useTooltip(ToolTipContent.tooltipSAT)"
          :card-tooltip="useTooltip(ToolTipContent.tooltipSATInfo)"
        >
          <template #icon>
            <cb-icon-training :width="16" />
          </template>
          <template #footer>
            <cl-data-card-footer-loading v-if="loading" />
            <cl-data-card-footer-big-number v-else>
              <span
                :class="
                  atRiskCustomersSat
                    ? 'tw-text-system-error-500'
                    : 'tw-text-system-success-500'
                "
                :data-test-id="
                  psAtRisk
                    ? `${tier}-sat-info-number-negative`
                    : `${tier}-sat-info-number-positive`
                "
              >
                {{ atRiskCustomersSat }}
              </span>
              <cl-data-card-footer-small-number>
                <span :data-test-id="`${tier}-sat-total`">
                  / {{ protectedCustomersSat }}</span
                >
              </cl-data-card-footer-small-number>
            </cl-data-card-footer-big-number>
          </template>
        </cl-data-card>
      </router-link>
    </div>
    <div
      v-else
      class="tw-col-span-3 max-xl:tw-col-span-5 max-md:tw-col-span-10"
    >
      <DashboardEmptyCard v-if="isFeatureAvailable" type="SAT" />
      <DashboardEmptyCardLegacy v-else type="SAT" />
    </div>
    <div
      v-if="isSolutionActive(ARC_SERVICE) && isFeatureAvailable"
      class="tw-col-span-3 max-xl:tw-col-span-5 max-md:tw-col-span-10"
      :data-test-id="`${tier}-overview-card-email-archive-route`"
    >
      <router-link
        v-slot="{ href, navigate }"
        :to="
          buildOrgUrl(
            isMspRoute ? 'msp' : 'customer',
            globalOrganisationId,
            'email_archiving',
          )
        "
        custom
      >
        <cl-data-card
          class="tw-h-full"
          data-test-id="dashboard-email-archiving-card"
          :link="{
            href,
            navigate,
            label: $t('email-archiving-label'),
          }"
          :icon-tooltip="useTooltip(ToolTipContent.tooltipEmailArchiving)"
        >
          <template #icon>
            <cb-icon-email-archiving :width="16" />
          </template>
          <template #footer>
            <div class="tw-flex tw-justify-end">
              <cl-button
                variant="secondary"
                :data-test-id="`${tier}-dashboard-card-email-archiving-more`"
                @click.prevent="openExternalLink"
              >
                <span class="tw-mr-2">{{ t("open-email-archiving") }}</span>
                <cb-icon-external-link :width="20" color="currentColor" />
              </cl-button>
            </div>
          </template>
        </cl-data-card>
      </router-link>
    </div>
    <div
      v-else-if="!isSolutionActive(ARC_SERVICE) && isFeatureAvailable"
      class="tw-col-span-3 max-xl:tw-col-span-5 max-md:tw-col-span-10"
    >
      <DashboardEmptyCard v-if="isFeatureAvailable" type="EMAIL_ARCHIVING" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, reactive } from "vue";
import { storeToRefs } from "pinia";
import { useHistoryStore } from "@/store/history.store";
import { useArcTitanStore } from "@/modules/ArcTitan/store/arcTitan.store";
import { useDashCardsStore } from "@/modules/Dashboard/store/cards.store";
import { useLicenceStore } from "@/modules/Usage/store/licence.store";
import {
  PHISHING_SIMULATION,
  PHISH_SERVICE,
  SAT_SERVICE,
  ARC_SERVICE,
} from "@/utils/services";
import { useGlobalOrganisationIDBasedOnRoute } from "@/composables/useGlobalOrganisationIDBasedOnRoute";
import buildOrgUrl from "@/utils/navigation/orgUrl";
import { useIsMspRoute } from "@/composables/useIsMspRoute";
import dayjs from "dayjs";
import { CUSTOMER, MSP } from "@/constants";
import { useActiveSolutions } from "@/composables/useActiveSolutions";
import DashboardEmptyCard from "./components/DashboardEmptyCard.vue";
import DashboardEmptyCardLegacy from "./components/DashboardEmptyCardLegacy.vue";
import { useI18n } from "vue-i18n";
import { formatNumberCompat } from "@/utils/formatNumberCompat";
import { useFlag } from "@/composables/useFlags/useFlag";

const { t } = useI18n();

const { isSolutionActive } = useActiveSolutions();
const { isFeatureAvailable } = useFlag("DASHBOARD_EMAIL_ARCHIVING_CARD");

const props = defineProps({
  startDate: {
    type: String,
    required: true,
  },
});
const { isMspRoute } = useIsMspRoute();
const tier = isMspRoute.value ? MSP : CUSTOMER;

const ToolTipContent = {
  tooltipEmailSecurity: {
    content: t("email-security-solution"),
    placement: "top",
  },
  tooltipEmailSecurityInfo: {
    content: isMspRoute.value
      ? t("incidents-card-msp")
      : t("incidents-card-cust"),
    placement: "top",
  },
  tooltipPhishingSimulation: {
    content: t("phishing-simulation-label"),
    placement: "top",
  },
  tooltipPhishingSimulationInfo: {
    content: isMspRoute.value
      ? t("at-risk-protected-phs")
      : t("lures-clicked-lures-sent"),
    placement: "top",
  },
  tooltipSAT: {
    content: t("sat-label"),
    placement: "top",
  },

  tooltipSATInfo: {
    content: t("at-risk-protected-sat"),
    placement: "top",
  },
  tooltipEmailArchiving: {
    content: t("email-archiving-label"),
    placement: "top",
  },
};
const { globalOrganisationId } = useGlobalOrganisationIDBasedOnRoute();
const incidentsHref = computed(() =>
  buildOrgUrl(
    isMspRoute.value ? "msp" : "customer",
    globalOrganisationId.value,
    "email_security/incidents",
  ),
);

const loading = ref(true);
const isLicenceCountLoading = ref(true);

const historyStore = useHistoryStore();
const arcTitanStore = useArcTitanStore();
const dashCardsStore = useDashCardsStore();
const licenceStore = useLicenceStore();

const { totalIncidents } = storeToRefs(historyStore);
const psAtRisk = computed(() => dashCardsStore.atRiskCustomers);
const psProtected = computed(() => dashCardsStore.protectedCustomers);
const atRiskCustomersSat = computed(() => dashCardsStore.atRiskSat);
const protectedCustomersSat = computed(() => dashCardsStore.protectedSat);

historyStore.setTotalEmailsAnalyzed(0);
historyStore.setTotalIncidents(0);
const useTooltip = ({ content = "", placement = "top" } = {}) => {
  return reactive({ content, placement });
};
const fetchIncidentsAndEmails = async () => {
  try {
    loading.value = true;

    const params = {
      start_date: props.startDate,
      end_date: dayjs().format("YYYY-MM-DD"),
      organisationType: MSP,
    };

    await Promise.allSettled([
      dashCardsStore.getAtRiskAndProtected({
        globalOrganisationID: globalOrganisationId.value,
      }),
      historyStore.getTotalEmailsAnalyzed({
        organisationId: globalOrganisationId.value,
        params,
      }),
      historyStore.getTotalIncidents({
        organisationId: globalOrganisationId.value,
        params,
      }),
      await dashCardsStore.getSatCardData({
        globalOrganisationID: globalOrganisationId.value,
      }),
    ]);
  } finally {
    loading.value = false;
  }
};

const fetchLicences = async () => {
  isLicenceCountLoading.value = true;
  try {
    await Promise.all([
      licenceStore.listAllLicences({
        uuid: globalOrganisationId.value,
      }),
    ]);
  } finally {
    isLicenceCountLoading.value = false;
  }
};

const openExternalLink = () => {
  window.open(arcTitanStore.link ?? undefined, "_blank");
};

watch(
  () => props.startDate,
  () => {
    fetchIncidentsAndEmails();
    fetchLicences();
  },
  { immediate: true },
);
</script>
